import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { ActionNode, DocumentSchema } from '../../documents/types';
import { DocumentButton } from '../../documents/form/actions/DocumentButton';
import { cn } from '../../../lib/utils';

interface CaseWorkspaceFooterProps {
    schema: DocumentSchema | null;
    onPreview: () => Promise<void> | void;
    onSaveDraft: () => Promise<void> | void;
    onComplete: () => Promise<void> | void;
    isSaving: boolean;
    isCompleting: boolean;
    isPreviewing: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    hideSaveDraft?: boolean;
}

const resolveSchemaCardClassName = (
    schema?: Pick<DocumentSchema, 'style'> | null
) =>
    cn(
        schema?.style?.noBorder && 'border-none shadow-none',
        schema?.style?.noBackground && 'bg-transparent',
        schema?.style?.className
    );

const getActionHandler = (
    actionKey: string,
    handlers: {
        onPreview: () => Promise<void> | void;
        onSaveDraft: () => Promise<void> | void;
        onComplete: () => Promise<void> | void;
    }
) => {
    if (actionKey === 'preview') return handlers.onPreview;
    if (actionKey === 'saveDraft') return handlers.onSaveDraft;
    if (actionKey === 'completeReport') return handlers.onComplete;
    return null;
};

export const CaseWorkspaceFooter: React.FC<CaseWorkspaceFooterProps> = ({
    schema,
    onPreview,
    onSaveDraft,
    onComplete,
    isSaving,
    isCompleting,
    isPreviewing,
    disabled = false,
    readOnly = false,
    hideSaveDraft = false,
}) => {
    if (!schema) {
        return (
            <Card>
                <CardContent className="pt-6 text-sm text-muted-foreground">
                    Footer actions schema is unavailable.
                </CardContent>
            </Card>
        );
    }

    const actionNodes: ActionNode[] = schema.sections
        .flatMap((section) => section.fields)
        .filter((field): field is ActionNode => field.kind === 'action');
    const showFooterHeader = schema.id !== 'form-actions';

    return (
        <Card className={resolveSchemaCardClassName(schema)}>
            {showFooterHeader && (
                <CardHeader className="border-b pb-5">
                    <CardTitle className="text-xl tracking-tight">
                        {schema.title}
                    </CardTitle>
                </CardHeader>
            )}
            <CardContent className={showFooterHeader ? 'pt-5' : 'pt-6'}>
                <div className="flex flex-wrap -mx-2">
                    {actionNodes.map((actionNode) => {
                        const actionKey =
                            actionNode.functionKey || actionNode.id;
                        const handler = getActionHandler(actionKey, {
                            onPreview,
                            onSaveDraft,
                            onComplete,
                        });

                        if (!handler) {
                            return null;
                        }

                        if (hideSaveDraft && actionKey === 'saveDraft') {
                            return null;
                        }

                        if (readOnly && actionKey !== 'preview') {
                            return null;
                        }

                        const isLoading =
                            (actionKey === 'preview' && isPreviewing) ||
                            (actionKey === 'saveDraft' && isSaving) ||
                            (actionKey === 'completeReport' && isCompleting);
                        const disableAction =
                            disabled ||
                            (actionKey === 'preview'
                                ? isPreviewing
                                : isSaving || isCompleting || isPreviewing);
                        const styledActionNode: ActionNode = {
                            ...actionNode,
                            style: {
                                ...actionNode.style,
                                color:
                                    actionKey === 'completeReport'
                                        ? 'default'
                                        : 'outline',
                                className: cn(
                                    'min-h-11 justify-center',
                                    actionNode.style?.className
                                ),
                            },
                        };

                        return (
                            <div
                                key={actionNode.id}
                                className="px-2 mb-2 max-sm:!w-full"
                                style={{
                                    width:
                                        actionNode.style?.layout?.width ||
                                        '100%',
                                }}
                            >
                                <DocumentButton
                                    field={styledActionNode}
                                    onClick={handler}
                                    loading={isLoading}
                                    disabled={disableAction}
                                />
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
};

export default CaseWorkspaceFooter;
